<template>
  <div>
    <!-- Header -->
    <v-row class="ma-0 my-4 align-center justify-space-between">
      <h1 class="text-h5 font-weight-bold">Club de Descuentos</h1>
      <v-btn v-if="can('discount-club:create')" rounded color="primary" depressed @click="openCreateDialog">
        <v-icon left>mdi-plus</v-icon>
        <span class="d-none d-md-inline">Agregar alianza</span>
      </v-btn>
    </v-row>

    <!-- Buscador + Filtros -->
    <v-row class="ma-0 mb-3" align="center">
      <v-col cols="12" md="5" class="pa-0 pr-md-3">
        <v-text-field
          v-model="search"
          label="Buscar"
          placeholder="Nombre"
          dense
          hide-details
          outlined
          append-icon="mdi-magnify"
          @click:append="loadAlliances"
          @keyup.enter="loadAlliances"
          @keyup="onSearchKeyup"
        />
      </v-col>
      <v-col cols="12" md="auto" class="pa-0">
        <v-btn-toggle
          v-model="activeFilter"
          dense
          color="primary"
          borderless
        >
          <v-btn :value="null" small>Todos</v-btn>
          <v-btn :value="true" small>Activos</v-btn>
          <v-btn :value="false" small>Inactivos</v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>

    <!-- Tabla -->
    <v-data-table
      :headers="headers"
      :items="alliances"
      :loading="loadingData"
      :options.sync="options"
      :items-per-page="limit"
      :server-items-length="totalItems"
      class="elevation-1"
      :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
    >
      <template v-slot:item.imageCardUrl="{ item }">
        <v-avatar size="40" class="my-1">
          <v-img :src="item.imageCardUrl" />
        </v-avatar>
      </template>

      <template v-slot:item.type="{ item }">
        <v-chip x-small :color="item.type === 'exclusive' ? 'deep-purple' : 'primary'" dark>
          {{ item.type === 'exclusive' ? 'Exclusiva' : 'Pública' }}
        </v-chip>
      </template>

      <template v-slot:item.active="{ item }">
        <v-icon color="green" v-if="item.active">mdi-check-circle</v-icon>
        <v-icon color="red" v-else>mdi-close-circle</v-icon>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="mx-1" fab dark depressed x-small color="orange"
              v-bind="attrs" v-on="on"
              @click="openEditDialog(item)"
            >
              <v-icon small>{{ canUpdate ? 'mdi-pencil' : 'mdi-eye' }}</v-icon>
            </v-btn>
          </template>
          <span>{{ canUpdate ? 'Editar' : 'Ver detalle' }}</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <!-- Dialog Crear / Editar -->
    <v-dialog v-model="showDialog" max-width="960" @keydown.esc="closeDialog">
      <v-card>
        <v-card-title class="text-uppercase pb-0 d-flex align-center justify-space-between">
          <div class="d-flex align-center" style="gap:16px">
            <v-switch
              v-model="form.active"
              :disabled="!canSaveBenefit"
              dense
              hide-details="auto"
              class="mt-0 pt-0 flex-shrink-0"
            />
            <span>{{ dialogTitle }}</span>
          </div>
          <v-btn icon small @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <hr class="mx-6 my-3" />
        <v-card-text class="pb-0">
          <v-form ref="form" v-model="formValid" :disabled="!canSaveBenefit">
            <!-- Fila 1: Nombre | País -->
            <v-row class="mb-3">
              <v-col cols="12" md="6">
                <label class="field-label">Nombre de la empresa</label>
                <v-text-field
                  v-model="form.name"
                  placeholder="Nombre de la empresa"
                  outlined dense hide-details="auto"
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>
              <v-col cols="12" md="6">
                <label class="field-label">País</label>
                <v-select
                  v-model="form.countryId"
                  placeholder="Seleccionar país"
                  :items="countries"
                  item-text="text"
                  item-value="id"
                  :loading="loadingCountries"
                  outlined dense hide-details="auto"
                  :rules="[v => !!v || 'Requerido']"
                  no-data-text="Sin resultados"
                />
              </v-col>
            </v-row>

            <!-- Fila 2: Tipo de beneficio | Región -->
            <v-row class="mb-3">
              <v-col cols="12" md="6">
                <label class="field-label">Tipo de beneficio</label>
                <v-text-field
                  v-model="form.benefits_type"
                  placeholder="ej: Hasta 20% DCTO"
                  outlined dense hide-details="auto"
                  maxlength="50"
                  counter="50"
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>
              <v-col cols="12" md="6">
                <label class="field-label">Región donde aplica</label>
                <v-text-field
                  v-model="form.location"
                  placeholder="Ej: Región Metropolitana"
                  outlined dense hide-details="auto"
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>
            </v-row>

            <!-- Fila 3: Tipo de acceso -->
            <v-row class="mb-3">
              <v-col cols="12" md="6">
                <label class="field-label">Tipo de acceso</label>
                <v-btn-toggle
                  v-model="form.type"
                  mandatory
                  dense
                  color="primary"
                  class="mt-1"
                  @change="onTypeChange"
                >
                  <v-btn value="public" small>Pública</v-btn>
                  <v-btn value="exclusive" small>Exclusiva</v-btn>
                </v-btn-toggle>
              </v-col>
              <v-col v-if="form.type === 'exclusive'" cols="12" md="6">
                <label class="field-label">Empresa exclusiva</label>
                <v-autocomplete
                  v-model="form.exclusiveCompanyId"
                  :items="companiesList"
                  item-text="name"
                  item-value="id"
                  :loading="loadingCompanies"
                  outlined dense hide-details="auto"
                  placeholder="Buscar empresa"
                  :rules="[v => !!v || 'Requerido']"
                  no-data-text="Sin resultados"
                  clearable
                />
              </v-col>
            </v-row>

            <!-- Fila 4: Descripción | Cómo usarlo -->
            <v-row class="mb-3">
              <v-col cols="12" md="6">
                <label class="field-label">Descripción</label>
                <v-text-field
                  v-model="form.description"
                  placeholder="Bajada de texto"
                  outlined dense hide-details="auto"
                  maxlength="80"
                  counter="80"
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>
              <v-col cols="12" md="6">
                <label class="field-label">Cómo usarlo</label>
                <v-text-field
                  v-model="form.how_to_use"
                  placeholder="Instrucciones de uso"
                  outlined dense hide-details="auto"
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>
            </v-row>

            <!-- Fila 4: Sitio web | Logotipo -->
            <v-row class="mb-3">
              <v-col cols="12" md="6">
                <label class="field-label">Sitio web / Dónde usarlo</label>
                <v-text-field
                  v-model="form.alliance_url"
                  placeholder="https://..."
                  outlined dense hide-details="auto"
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>
              <v-col cols="12" md="6">
                <label class="field-label">
                  Logotipo empresa{{ editingId ? ' — dejar vacío para mantener el actual' : '' }}
                </label>
                <div class="d-flex align-center">
                  <v-file-input
                    v-model="imageCardFile"
                    accept="image/jpeg,image/png"
                    placeholder="Seleccionar imagen JPG"
                    outlined dense hide-details="auto"
                    prepend-icon="" prepend-inner-icon="mdi-image"
                    :rules="!editingId ? [v => !!v || 'Requerido'] : []"
                    @change="onImageChange('imageCard', $event)"
                  />
                  <v-avatar
                    v-if="imageCardPreview"
                    size="52" class="ml-3 flex-shrink-0"
                    style="border: 2px solid #e0e0e0"
                  >
                    <v-img :src="imageCardPreview" />
                  </v-avatar>
                </div>
                <p class="caption grey--text mt-1 mb-0">Puede ser JPG o PNG, 700×800px, máx 1MB</p>
                <p v-if="imageErrors.imageCard" class="caption red--text mt-1 mb-0">{{ imageErrors.imageCard }}</p>
              </v-col>
            </v-row>

            <!-- Fila 5: Características (ancho completo, sin saltos de línea) -->
            <v-row>
              <v-col cols="12">
                <label class="field-label">Característica del beneficio</label>
                <v-textarea
                  v-model="form.benefit_details"
                  placeholder="Características del beneficio"
                  outlined dense hide-details="auto"
                  rows="2"
                  no-resize
                  :rules="[v => !!v || 'Requerido']"
                  @keydown.enter.prevent
                />
              </v-col>
            </v-row>

            <p v-if="formError" class="caption red--text mt-3 mb-0">{{ formError }}</p>
          </v-form>

          <!-- Beneficios -->
          <div>
            <hr class="mx-0 my-4" />
            <div class="d-flex align-center justify-space-between mb-3">
              <p class="subtitle-2 grey--text text--darken-1 mb-0">Beneficios</p>
              <v-btn
                v-if="!showBenefitForm && canSaveBenefit"
                small depressed rounded color="primary"
                @click="openAddBenefit"
              >
                <v-icon left small>mdi-plus</v-icon>Agregar beneficio
              </v-btn>
            </div>

            <!-- Lista de beneficios existentes -->
            <v-card v-if="benefits.length" outlined class="mb-3">
              <v-list dense>
                <template v-for="(b, i) in benefits">
                  <v-list-item :key="b.id" v-show="editingBenefitId !== b.id">
                    <v-list-item-content>
                      <v-list-item-title class="text-body-2">{{ b.benefit_code || '(sin código)' }}</v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        <span v-if="b.details">{{ b.details }}</span>
                        <span v-if="b.expiresAt" :class="b.details ? 'ml-2' : ''">· Vence: {{ b.expiresAt.substr(0, 10) }}</span>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action class="flex-row align-center my-0" style="gap:4px">
                      <v-btn v-if="canSaveBenefit" icon x-small color="orange" @click="openEditBenefit(b)">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                  <v-divider :key="'d-' + i" v-if="i < benefits.length - 1" />
                </template>
              </v-list>
            </v-card>
            <p v-else-if="!showBenefitForm" class="text-caption grey--text mb-3">Sin beneficios agregados.</p>

            <!-- Formulario inline de beneficio -->
            <v-expand-transition>
              <v-card v-if="showBenefitForm" outlined class="pa-4">
                <div class="d-flex align-center mb-3" style="gap:12px">
                  <v-switch
                    v-model="benefitForm.active"
                    dense hide-details="auto"
                    class="mt-0 pt-0 flex-shrink-0"
                  />
                  <span class="subtitle-2">{{ editingBenefitId ? 'Editar beneficio' : 'Nuevo beneficio' }}</span>
                </div>
                <v-form ref="benefitForm" v-model="benefitFormValid">
                  <v-row>
                    <v-col cols="12" md="6">
                      <label class="field-label">Código de descuento</label>
                      <v-text-field
                        v-model="benefitForm.benefit_code"
                        placeholder="Código de descuento"
                        outlined dense hide-details="auto"
                        :rules="[v => !!v || 'El código de descuento es requerido']"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <label class="field-label">Detalle del beneficio (opcional)</label>
                      <v-text-field
                        v-model="benefitForm.details"
                        placeholder="ej: $2.500 DCTO en tu cajita de 1 mes"
                        outlined dense hide-details="auto"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <label class="field-label">Fecha de vencimiento (opcional)</label>
                      <v-menu
                        v-model="expiresAtMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="benefitForm.expiresAt"
                            placeholder="Seleccionar fecha"
                            outlined dense hide-details="auto"
                            readonly append-icon="mdi-calendar"
                            clearable
                            v-bind="attrs" v-on="on"
                            @click:clear="benefitForm.expiresAt = null"
                          />
                        </template>
                        <v-date-picker
                          v-model="benefitForm.expiresAt"
                          locale="es"
                          @input="expiresAtMenu = false"
                        />
                      </v-menu>
                    </v-col>
                  </v-row>
                  <p v-if="benefitFormError" class="caption red--text mt-2 mb-0">{{ benefitFormError }}</p>
                </v-form>
                <div class="d-flex justify-end mt-3" style="gap:8px">
                  <v-btn text color="primary" rounded small @click="closeBenefitForm">Cancelar</v-btn>
                  <v-btn
                    v-if="canSaveBenefit"
                    color="primary"
                    rounded
                    depressed
                    small
                    :loading="savingBenefit"
                    @click="submitBenefitForm"
                  >
                    Guardar
                  </v-btn>
                </div>
              </v-card>
            </v-expand-transition>
          </div>
        </v-card-text>
        <hr class="mx-6 my-3" />
        <v-card-actions class="justify-end px-6 pb-4">
          <v-btn text color="primary" rounded @click="closeDialog">Cancelar</v-btn>
          <v-btn
            v-if="can(editingId ? 'discount-club:update' : 'discount-club:create')"
            color="primary" rounded depressed
            :loading="saving"
            :disabled="saving || benefits.length === 0"
            @click="submitForm"
          >
            {{ editingId ? 'Guardar cambios' : 'Crear alianza' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>



    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" bottom :timeout="4000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>

    <overlay :value="false" />
  </div>
</template>

<script>
import AlliancesService from '../services/alliances.service';
import CountryService from '../services/countries.service';
import CompaniesService from '../services/companie.service';
import Overlay from '../components/Overlay.vue';
import { can } from '../utils/permissions';

const MAX_SIZE_BYTES = 1048576;

const emptyBenefitForm = () => ({
  benefit_code: '',
  details: '',
  expiresAt: null,
  active: true
});

const emptyForm = () => ({
  name: '',
  benefits_type: '',
  description: '',
  how_to_use: '',
  benefit_details: '',
  alliance_url: '',
  location: '',
  active: true,
  countryId: null,
  type: 'public',
  exclusiveCompanyId: null
});

export default {
  name: 'ClubDescuentos',
  components: { Overlay },
  data() {
    return {
      alliances: [],
      loadingData: false,
      options: {},
      page: 1,
      limit: 10,
      totalItems: 0,
      search: '',
      activeFilter: null,

      headers: [
        { text: '', value: 'imageCardUrl', sortable: false, width: '60px' },
        { text: 'Nombre', value: 'name', sortable: false },
        { text: 'Tipo de beneficio', value: 'benefits_type', sortable: false },
        { text: 'Región', value: 'location', sortable: false },
        { text: 'Tipo', value: 'type', sortable: false, align: 'center' },
        { text: 'Activo', value: 'active', sortable: false, align: 'center' },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
      ],

      // Dialog crear/editar
      showDialog: false,
      editingId: null,
      formValid: false,
      saving: false,
      form: emptyForm(),
      formError: '',

      // Imágenes
      imageCardFile: null,
      imageCardBase64: null,
      imageCardPreview: null,
      imageErrors: { imageCard: '' },

      // Dialog eliminar

      // Países
      countries: [],
      loadingCountries: false,

      // Empresas (para alianzas exclusivas)
      companiesList: [],
      loadingCompanies: false,

      snackbar: { show: false, text: '', color: 'success' },

      // Gestión de benefits dentro del dialog de edición
      benefits: [],
      showBenefitForm: false,
      editingBenefitId: null,
      benefitFormValid: false,
      benefitForm: emptyBenefitForm(),
      benefitFormError: '',
      savingBenefit: false,
      expiresAtMenu: false,
    };
  },
  computed: {
    // Benefits of a new alliance are staged inside the create flow (spec 132).
    canSaveBenefit() {
      return can(this.editingId ? 'discount-club:update' : 'discount-club:create');
    },
    canUpdate() {
      return can('discount-club:update');
    },
    // View-only admins open the same dialog read-only to see the benefits.
    dialogTitle() {
      if (!this.editingId) return 'Nueva alianza';
      return this.canUpdate ? 'Editar alianza' : 'Ver alianza';
    }
  },
  mounted() {
    this.loadCountries();
  },
  watch: {
    options: {
      handler() {
        this.page = this.options.page;
        this.limit = this.options.itemsPerPage;
        this.loadAlliances();
      },
      deep: true
    },
    activeFilter() {
      this.page = 1;
      this.loadAlliances();
    }
  },
  methods: {
    can,
    async loadCompanies() {
      if (this.companiesList.length) return;
      this.loadingCompanies = true;
      try {
        const res = await CompaniesService.getCompanies(1, 200, '', 'active');
        if (res && res.status === 'ok') {
          this.companiesList = res.data.companies ?? res.data;
        }
      } catch {
        // silencioso
      }
      this.loadingCompanies = false;
    },

    onTypeChange(type) {
      if (type === 'exclusive') {
        this.loadCompanies();
      } else {
        this.form.exclusiveCompanyId = null;
      }
    },

    async loadCountries() {
      this.loadingCountries = true;
      try {
        const res = await CountryService.getAllCountries(true);
        if (res && res.status === 'ok') {
          this.countries = res.data.map(c => ({
            text: `${c.name ?? ''} ${c.flagUrlImage ?? ''}`.trim(),
            id: c.id,
            iso: c.iso
          }));
        }
      } catch {
        // silencioso — la tabla de países no es bloqueante
      }
      this.loadingCountries = false;
    },

    async loadAlliances() {
      this.loadingData = true;
      const res = await AlliancesService.getAll(this.page, this.limit, this.search, this.activeFilter);
      if (res.status === 'ok') {
        this.alliances = res.data.alliances ?? res.data;
        this.totalItems = res.data.totalItems ?? res.data.count ?? this.alliances.length;
      }
      this.loadingData = false;
    },

    onSearchKeyup() {
      if (!this.search || this.search.length === 0) {
        this.loadAlliances();
      }
    },

    openCreateDialog() {
      this.editingId = null;
      this.form = emptyForm();
      this.clearImages();
      this.benefits = [];
      this.closeBenefitForm();
      this.formError = '';
      this.$nextTick(() => { if (this.$refs.form) this.$refs.form.resetValidation(); });
      this.showDialog = true;
    },

    openEditDialog(alliance) {
      this.editingId = alliance.id;
      this.form = {
        name: alliance.name,
        benefits_type: alliance.benefits_type,
        description: alliance.description,
        how_to_use: alliance.how_to_use || '',
        benefit_details: alliance.benefit_details || '',
        alliance_url: alliance.alliance_url,
        location: alliance.location,
        active: alliance.active,
        countryId: alliance.countryId,
        type: alliance.type || 'public',
        exclusiveCompanyId: alliance.exclusiveCompanyId || null
      };
      if (alliance.type === 'exclusive') this.loadCompanies();
      this.clearImages();
      if (alliance.imageCardUrl) this.imageCardPreview = alliance.imageCardUrl;
      this.benefits = (alliance.benefits || []).slice();
      this.closeBenefitForm();
      this.formError = '';
      this.$nextTick(() => { if (this.$refs.form) this.$refs.form.resetValidation(); });
      this.showDialog = true;
    },

    closeDialog() {
      this.showDialog = false;
      this.editingId = null;
      this.form = emptyForm();
      this.clearImages();
      this.benefits = [];
      this.closeBenefitForm();
      this.formError = '';
    },

    clearImages() {
      this.imageCardFile = null;
      this.imageCardBase64 = null;
      this.imageCardPreview = null;
      this.imageErrors = { imageCard: '' };
    },

    async onImageChange(key, file) {
      this.imageErrors[key] = '';
      this[`${key}Base64`] = null;
      this[`${key}Preview`] = null;

      if (!file) return;

      if (file.size > MAX_SIZE_BYTES) {
        this.imageErrors[key] = 'La imagen supera el tamaño máximo permitido de 1 MB';
        this[`${key}File`] = null;
        return;
      }

      const { base64, dataUrl } = await this.fileToBase64(file);
      this[`${key}Base64`] = base64;
      this[`${key}Preview`] = dataUrl;
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          resolve({ base64: reader.result.split(',')[1], dataUrl: reader.result });
        };
        reader.onerror = err => reject(err);
      });
    },

    async submitForm() {
      this.formError = '';
      if (!this.$refs.form.validate()) return;
      if (Object.values(this.imageErrors).some(e => !!e)) return;

      if (!this.editingId && !this.imageCardBase64) {
        this.formError = 'La imagen de la card es requerida para crear una alianza';
        return;
      }

      if (this.benefits.length === 0) {
        this.formError = 'Debes agregar al menos un beneficio antes de guardar la alianza';
        return;
      }

      this.saving = true;

      const payload = {
        name: this.form.name,
        benefits_type: this.form.benefits_type,
        description: this.form.description,
        how_to_use: this.form.how_to_use || null,
        benefit_details: this.form.benefit_details || null,
        alliance_url: this.form.alliance_url,
        location: this.form.location,
        active: this.form.active,
        countryId: this.form.countryId,
        type: this.form.type || 'public',
        exclusiveCompanyId: this.form.type === 'exclusive' ? this.form.exclusiveCompanyId : null,
        ...(this.imageCardBase64 && { imageCardUrl: this.imageCardBase64 })
      };

      if (this.editingId) {
        const res = await AlliancesService.update(this.editingId, payload);
        this.handleSaveResponse(res, 'Alianza actualizada correctamente');
      } else {
        const res = await AlliancesService.create(payload);
        if (res.status === 'ok') {
          const newId = res.data.id;
          for (const b of this.benefits) {
            await AlliancesService.createBenefit(newId, {
              benefit_code: b.benefit_code || null,
              details: b.details,
              expiresAt: b.expiresAt || null,
              active: b.active,
              allianceId: newId
            });
          }
          this.showSnackbar('Alianza creada correctamente', 'success');
          this.closeDialog();
          this.loadAlliances();
        } else {
          this.formError = res.message || 'Ocurrió un error, intenta nuevamente';
        }
      }

      this.saving = false;
    },

    handleSaveResponse(res, successMessage) {
      if (res.status === 'ok') {
        this.showSnackbar(successMessage, 'success');
        this.closeDialog();
        this.loadAlliances();
      } else {
        this.formError = res.message || 'Ocurrió un error, intenta nuevamente';
      }
    },

    showSnackbar(text, color) {
      this.snackbar = { show: true, text, color };
    },

    openAddBenefit() {
      this.editingBenefitId = null;
      this.benefitForm = emptyBenefitForm();
      this.benefitFormError = '';
      this.$nextTick(() => { if (this.$refs.benefitForm) this.$refs.benefitForm.resetValidation(); });
      this.showBenefitForm = true;
    },

    openEditBenefit(benefit) {
      this.editingBenefitId = benefit.id;
      this.benefitForm = {
        benefit_code: benefit.benefit_code || '',
        details: benefit.details,
        expiresAt: benefit.expiresAt ? benefit.expiresAt.substr(0, 10) : null,
        active: benefit.active
      };
      this.benefitFormError = '';
      this.$nextTick(() => { if (this.$refs.benefitForm) this.$refs.benefitForm.resetValidation(); });
      this.showBenefitForm = true;
    },

    closeBenefitForm() {
      this.showBenefitForm = false;
      this.editingBenefitId = null;
      this.benefitForm = emptyBenefitForm();
      this.benefitFormError = '';
      this.expiresAtMenu = false;
    },

    async submitBenefitForm() {
      this.benefitFormError = '';
      if (!this.$refs.benefitForm || !this.$refs.benefitForm.validate()) return;

      this.savingBenefit = true;
      const payload = {
        benefit_code: this.benefitForm.benefit_code || null,
        details: this.benefitForm.details,
        expiresAt: this.benefitForm.expiresAt || null,
        active: this.benefitForm.active
      };

      // Modo crear: staging local, se persiste al guardar la alianza
      if (!this.editingId) {
        if (this.editingBenefitId) {
          const idx = this.benefits.findIndex(b => b.id === this.editingBenefitId);
          if (idx !== -1) this.$set(this.benefits, idx, { ...payload, id: this.editingBenefitId });
        } else {
          this.benefits.push({ ...payload, id: `staged-${Date.now()}` });
        }
        this.closeBenefitForm();
        this.savingBenefit = false;
        return;
      }

      // Modo editar: persistir en API de inmediato
      if (this.editingBenefitId) {
        const res = await AlliancesService.updateBenefit(this.editingId, this.editingBenefitId, { ...payload, allianceId: this.editingId });
        if (res.status === 'ok') {
          const idx = this.benefits.findIndex(b => b.id === this.editingBenefitId);
          if (idx !== -1) this.$set(this.benefits, idx, res.data);
          this.showSnackbar('Beneficio actualizado', 'success');
          this.closeBenefitForm();
        } else {
          this.benefitFormError = res.message || 'Error al actualizar el beneficio';
        }
      } else {
        const res = await AlliancesService.createBenefit(this.editingId, { ...payload, allianceId: this.editingId });
        if (res.status === 'ok') {
          this.benefits.push(res.data);
          this.showSnackbar('Beneficio creado', 'success');
          this.closeBenefitForm();
        } else {
          this.benefitFormError = res.message || 'Error al crear el beneficio';
        }
      }

      this.savingBenefit = false;
    },

    async toggleBenefitActive(benefit) {
      if (!this.editingId || (typeof benefit.id === 'string' && benefit.id.startsWith('staged-'))) return;
      await AlliancesService.updateBenefit(this.editingId, benefit.id, {
        active: benefit.active,
        allianceId: this.editingId
      });
    }
  }
};
</script>

<style scoped>
.field-label {
  display: block;
  color: #0F5CB9;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
  line-height: 1.4;
}

::v-deep .v-text-field--outlined fieldset {
  border-color: #CFD3D4;
  border-radius: 8px;
}

::v-deep .v-text-field input::placeholder,
::v-deep .v-textarea textarea::placeholder {
  color: #ABAFB1;
  font-size: 14px;
  opacity: 1;
}

::v-deep .v-select__placeholder {
  color: #ABAFB1;
  font-size: 14px;
}
</style>
